import { useEffect, useRef, useState } from 'react';
import { markJuiRoot } from '../jui/root';
import { BUG_OWN_ATTR } from '../recorder';

/*
 * The help button in the host's frame (decision 12 of 29.10, facts report 219; facts Quint `report_window` П610): the host
 * gives a node of its frame (`launcher.slot`, the element or a selector — facts: `.jui-appbar-end` by the bell). While
 * the node is on the page the button lives in it and nowhere else, and `<html>` has `help-in-bar` (the host drops the
 * room it kept for the corner button); no node (not drawn yet, gone with a page change) — it floats in the corner as
 * before, without the class. Inside the node the widget keeps a root of its own (`.jaiora-bugs`, `data-bug-own`): scoped styles,
 * the library theme, and the shot and the screen mode see it as the widget's.
 */

export const HELP_IN_BAR = 'help-in-bar';

type Slot = Element | string | null | undefined;

function find(slot: Slot): Element | null {
  if (!slot) return null;
  if (typeof slot !== 'string') return slot.isConnected ? slot : null;
  try {
    return document.querySelector(slot);
  } catch {
    return null;
  }
}

/** The widget's root inside the host's node, or null while there is no node (or no selector). */
export function useHostSlot(selector: Slot): HTMLElement | null {
  const [slot, setSlot] = useState<Element | null>(() => find(selector));
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const rootRef = useRef<{ slot: Element; el: HTMLElement } | null>(null);

  useEffect(() => {
    if (!selector || typeof MutationObserver === 'undefined') {
      setSlot(null);
      return;
    }
    const sync = () => {
      const now = find(selector);
      // The host redrew the node's children and dropped ours: the same root goes back (its React tree is intact).
      const own = rootRef.current;
      if (own && now === own.slot && !own.el.isConnected) now.appendChild(own.el);
      setSlot((was) => (now === was ? was : now));
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [selector]);

  useEffect(() => {
    if (!slot) {
      setRoot(null);
      return;
    }
    const el = document.createElement('span');
    el.className = 'jaiora-bugs help-slot-root';
    el.setAttribute(BUG_OWN_ATTR, '');
    const unmark = markJuiRoot(el);
    slot.appendChild(el);
    rootRef.current = { slot, el };
    setRoot(el);
    document.documentElement.classList.add(HELP_IN_BAR);
    return () => {
      rootRef.current = null;
      document.documentElement.classList.remove(HELP_IN_BAR);
      unmark();
      el.remove();
    };
  }, [slot]);

  return slot ? root : null;
}
